import React from 'react';
import { KeyedMutator, mutate } from 'swr';
import useSWRMutation from 'swr/mutation';

import Loading from '@/components/ui/Loading';

import { StudentsResponse } from '@/lib/Students';
import { toggle } from '@/lib/ToggleStudent';

type EnrollItemProps = {
  ID: number;
  index: number;
  avatar_url: string;
  user_login: string;
  isSelected: boolean;
  token: string;
  submissionPerPage: number;
  submissionPage: number;
  setSubmissionPage: React.Dispatch<React.SetStateAction<number>>;
  mutateList: KeyedMutator<StudentsResponse>;
  program?: string;
};

const EnrollItem = React.memo(
  ({ ID, index, avatar_url, user_login, isSelected, token, submissionPerPage, submissionPage, setSubmissionPage, mutateList, program }: EnrollItemProps) => {
    const { trigger, isMutating } = useSWRMutation('/wp-json/wall-of-fame/v1/enroll/', toggle);

    const toggleStudentSelection = async (id: number, isSelected: boolean) => {
      try {
        await trigger({ id, isSelected, token, program }).then(() =>
          mutateList((response) => {
            const updatedData =
              response?.data.map((item) => ({
                ...item,
                isSelected: item.ID === id ? !item.isSelected : item.isSelected,
              })) || [];

            return {
              data: updatedData,
              enrolled_count: response?.enrolled_count || 0,
              total: response?.total || 0,
            };
          }, false)
        );

        await mutateList().then((d) => {
          if (d) {
            const newPage = Math.ceil(d.enrolled_count / submissionPerPage);
            if (newPage < submissionPage && newPage != 0) {
              setSubmissionPage(newPage);
            } else {
              mutate(['/wp-json/wall-of-fame/v1/submissions', token, submissionPage, submissionPerPage]);
            }
          }
        });
      } catch (error) {
        console.error('Failed to toggle student selection:', error);
      }
    };

    return (
      <div key={index} className='p-4 border-b border-[#EAECF0] flex justify-between items-center'>
        <div className='flex items-center gap-3 h-11 w-full'>
          <img src={avatar_url} alt={user_login} width={40} height={40} className='rounded-full' />
          <h2 className='text-sm font-normal w-full text-[#000] truncate hover:text-clip'>{user_login}</h2>
        </div>
        <div className='flex relative'>
          {isMutating ? (
            // <>Fix Css</>
            <div className='absolute -right-2 z-[4] bg-white inline-flex text-center h-6 w-10 -top-3 items-center cursor-pointer'>
              <Loading />
            </div>
          ) : (
            <label className='inline-flex gap-2 items-center cursor-pointer h-6'>
              <input type='checkbox' checked={isSelected} onChange={() => toggleStudentSelection(ID, isSelected)} className='sr-only peer' />
              <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-[#0000FF]"></div>
            </label>
          )}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.isSelected == nextProps.isSelected && nextProps.ID == prevProps.ID;
  }
);

export default EnrollItem;
